import { trackImageSliderModelPage } from "common/analytics"
import React, { FC, useEffect, useState } from "react"
import { Direction, Range } from "react-range"
import { IThumbProps } from "react-range/lib/types"
import styled from "styled-components"
import { calculateAspectRatio, isFunction } from "./utils"
// https://github.com/mattrothenberg/react-comparison-slider

export interface ComparisonSliderHandleProps extends Omit<IThumbProps, "key"> {
  isFocused: boolean
}

export type ComparisonSliderProps = ComparisonSliderStatefulProps &
  ComparisonSliderCommonProps

interface ComparisonSliderCommonProps {
  itemOne: React.ReactNode | (({ value }: { value: number }) => React.ReactNode)
  itemTwo: React.ReactNode | (({ value }: { value: number }) => React.ReactNode)
  aspectRatio: number | string | null
  height?: number | string | null
  handleBefore?: React.ReactNode
  handleAfter?: React.ReactNode
  handle?: (props: ComparisonSliderHandleProps) => React.ReactNode
  orientation?: "vertical" | "horizontal"
  onlyHandleDraggable?: boolean
}

type ComparisonSliderStatefulProps =
  | { defaultValue: number; value?: never; onValueChange?: never }
  | {
      value: number
      onValueChange: (value: number) => void
      defaultValue?: never
    }

const PinnedDiv = styled.div`
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  position: absolute;
  direction: ltr;
`
const Track = styled(PinnedDiv)`
  background: transparent;
  z-index: 10;
`

const RangeWrap = styled(PinnedDiv)``

const Element = styled(PinnedDiv)`
  > * {
    height: 100%;
  }
  > *:not(style) + * {
    height: unset;
  }
`

const AspectWrap = styled.div`
  height: 0px;
  position: relative;
`

const HandleDecoration = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: all;

  // This causes the thumb to be located further down the
  // page. This does not reflect the current state of the Figma
  // since there is drift between the figma and how the client
  // wants the site to look.
  flex: 3 1 0%;

  :last-child {
    flex: 1 1 0%;
  }
`
const Handle = styled.div`
  width: 16px;
  height: 16px;
  background: ${(p) => p.theme.colors.white};
  border-radius: 100%;
  border: 1px solid transparent;
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.25);
    border-color: rgba(0, 0, 0, 1);
  }
`
const HandleCanvasWrap = styled.div`
  display: flex;
  &:focus {
    outline: none;
  }
  transform: translate(0, 20px);
`

const HandleWrap = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const DefaultHandle = (props: ComparisonSliderHandleProps) => (
  <Handle {...props} />
)

export const ComparisonSlider: FC<ComparisonSliderProps> = ({
  itemOne,
  itemTwo,
  aspectRatio,
  height = null,
  defaultValue,
  value,
  handle = DefaultHandle,
  handleBefore = null,
  handleAfter = null,
  orientation = "horizontal",
  onValueChange = () => {},
  onlyHandleDraggable = false,
}) => {
  const [focused, setFocused] = useState(false)
  const [localValue, setLocalValue] = useState(defaultValue)
  const [location, setLocation] = useState("")

  useEffect(() => {
    // only on client side
    if (typeof window === "undefined") return

    // Populate location of current page
    setLocation(window.location.pathname)

    const handleCanvasWrapElement = document.getElementById("handleCanvasWrap")
    const disableBackSwipe = (e: TouchEvent) => {
      trackImageSliderModelPage()
      e.preventDefault()
    }

    if (handleCanvasWrapElement) {
      handleCanvasWrapElement.addEventListener("touchstart", disableBackSwipe)
    }
    return () => {
      if (handleCanvasWrapElement) {
        handleCanvasWrapElement.removeEventListener(
          "touchstart",
          disableBackSwipe
        )
      }
    }
  }, [])

  const isControlled =
    typeof defaultValue === "undefined" && typeof value !== "undefined"

  const isHorizontal = orientation === "horizontal"

  const sliderValue = (isControlled ? value : localValue) as number

  const clipPath = isHorizontal
    ? `polygon(${sliderValue}% 0, 100% 0%, 100% 100%, ${sliderValue}% 100%)`
    : `polygon(0% 100%, 0% ${100 - sliderValue}%, 100% ${
        100 - sliderValue
      }%, 100% 100%)`

  const padding = height ? height : `${calculateAspectRatio(aspectRatio || 1)}%`
  const handleChange = (newValue: number) => {
    if (isControlled) {
      onValueChange(newValue)
    } else {
      setLocalValue(newValue)
    }
  }

  const ItemOne =
    itemOne && isFunction(itemOne as Function)
      ? (itemOne as Function)({ value: sliderValue })
      : itemOne

  const ItemTwo =
    itemTwo && isFunction(itemTwo as Function)
      ? (itemTwo as Function)({ value: sliderValue })
      : itemTwo

  const baseSlides = [
    <React.Fragment key={"one"}>{ItemOne}</React.Fragment>,
    <React.Fragment key={"two"}>{ItemTwo}</React.Fragment>,
  ]

  const direction = isHorizontal ? Direction.Right : Direction.Up

  const slides = isHorizontal ? baseSlides : baseSlides.reverse()

  return (
    <AspectWrap style={{ paddingBottom: `${padding}` }}>
      <React.Fragment>
        {slides.map((content, index) => {
          return (
            <Element
              style={{ clipPath: index === 1 ? clipPath : "" }}
              key={index}
            >
              {content}
            </Element>
          )
        })}
      </React.Fragment>

      <RangeWrap>
        <Range
          step={1}
          min={0}
          max={100}
          values={[sliderValue]}
          onChange={(values) => handleChange(values[0])}
          direction={direction}
          renderTrack={({ props, children }) => (
            <Track
              className=""
              {...props}
              style={{
                ...props.style,
                pointerEvents: onlyHandleDraggable ? "none" : "all",
              }}
            >
              {children}
            </Track>
          )}
          renderThumb={(params) => {
            const { key, ...thumbProps } = params.props
            const props: ComparisonSliderHandleProps = {
              ...thumbProps,
              isFocused: focused,
            }
            return (
              <HandleCanvasWrap
                key={key}
                id="handleCanvasWrap"
                {...thumbProps}
                style={{
                  ...params.props.style,
                  flexDirection: isHorizontal ? "column" : "row",
                  height: isHorizontal ? "100%" : "auto",
                  width: isHorizontal ? "auto" : "100%",
                }}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                role="presentation"
              >
                <HandleDecoration>{handleBefore}</HandleDecoration>
                <HandleWrap
                  style={{ pointerEvents: "all" }}
                  onClick={() => {
                    if (location?.indexOf("model") > 0) {
                      trackImageSliderModelPage()
                    }
                  }}
                >
                  {/* @ts-ignore */}
                  {handle(props)}
                </HandleWrap>
                <HandleDecoration>{handleAfter}</HandleDecoration>
              </HandleCanvasWrap>
            )
          }}
        />
      </RangeWrap>
    </AspectWrap>
  )
}
